import React, { useMemo, useState } from 'react';

import { isNil } from 'lodash-es';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import CheckmarkIcon from 'components/icons/check';
import Modal, { ModalContent, ModalHeading, ModalSize } from 'components/modal';
import TextButton from 'components/text-button';
import { GetLoyaltyCardsDocument, useDeleteLoyaltyCardMutation } from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { routes } from 'utils/routing';

import { ICardDeactivationModal } from './types';

const ErrorAnchor = styled.a`
  color: ${Styles.color.primary};
`;

const CenteredParagraph = styled.p`
  text-align: center;

  ${Styles.desktop`
    margin-top: 48px;
  `}
`;

const DeactivateCardButtonWrapper = styled.div`
  margin: 32px 0;
`;

const CardDeactivationFeedbackWrapper = styled.div<{ show: boolean }>`
  width: 100%;
  min-height: 128px;
  ${Styles.desktop`
    min-height: 110px;
  `}

  visibility: ${p => (p.show ? 'visible' : 'hidden')};

  padding: 1rem;
  font-size: 1rem;
  font-weight: ${Styles.fontWeight.heavy};

  & p {
    margin: 0;
  }
`;

const SuccessWrapper = styled(CardDeactivationFeedbackWrapper)`
  background-color: #eaede5;
  color: #4d6b29;

  display: flex;
`;

const FailedCardDeactivationFeedback = styled(CardDeactivationFeedbackWrapper)`
  background-color: #f9e7ea;
  color: #db5c6e;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
`;

const StyledCheckmarkIcon = styled(CheckmarkIcon)`
  height: 1.5rem;
  fill: #4d6b29;
`;

interface IFeedback {
  show: boolean;
}

const SuccessFeedback: React.FC<IFeedback> = ({ show }) => {
  const { formatMessage } = useIntl();
  return (
    <SuccessWrapper show={show}>
      <IconWrapper>
        <StyledCheckmarkIcon />
      </IconWrapper>
      <div>
        <span>{formatMessage({ id: 'success' })}</span>
        <p data-testid="card-deactivation-success-message">
          {formatMessage({ id: 'successfulCardDeactivation' })}
        </p>
      </div>
    </SuccessWrapper>
  );
};

const FailedFeedback: React.FC<IFeedback> = ({ show }) => {
  const { formatMessage } = useIntl();
  return (
    <FailedCardDeactivationFeedback show={show}>
      <p>
        {formatMessage(
          { id: 'cardDeactivationError' },
          {
            link: (
              <ErrorAnchor href={routes.support}>{formatMessage({ id: 'chatWithUs' })}</ErrorAnchor>
            ),
          }
        )}
      </p>
    </FailedCardDeactivationFeedback>
  );
};

const CardDeactivationModal: React.FC<ICardDeactivationModal> = ({
  onDismiss,
  modalAppearanceEventMessage = 'Deactivate Rewards Card',
}) => {
  const { formatMessage } = useIntl();
  const { activeCard } = useLoyaltyActiveCard();
  const gatewayEnabled = useFlag(LaunchDarklyFlag.ENABLE_GATEWAY_DELETE_LOYALTY_CARD);
  const [
    deleteLoyaltyCardMutation,
    { called, error, data, loading },
  ] = useDeleteLoyaltyCardMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetLoyaltyCardsDocument }],
  });
  const { logEvent } = useMParticleContext();

  const logLoyaltyCardDeactivatedEvent = (success: boolean, message?: string) => {
    logEvent(CustomEventNames.LOYALTY_CARD_DEACTIVATED, EventTypes.UserContent, {
      Response: success ? 'Successful' : 'Failure',
      'Response Description': success ? 'Successful' : message,
    });
  };

  const deleteLoyaltyCardSucceeded = useMemo(
    () => called && !error && !loading && !isNil(data && data.deleteLoyaltyCard),
    [data, called, error, loading]
  );

  const [showFeedback, setShowFeedback] = useState(false);

  const lostOrStolenText = formatMessage(
    { id: 'timsCardLostOrStolen' },
    {
      linebreak: (
        <>
          <br />
          <br />
        </>
      ),
    }
  );

  const handleDeactivation = async () => {
    if (!activeCard) {
      return;
    }

    setShowFeedback(false);

    try {
      await deleteLoyaltyCardMutation({
        variables: { cardId: activeCard.cardId, gatewayEnabled },
      });

      logLoyaltyCardDeactivatedEvent(true);
    } catch (e) {
      logLoyaltyCardDeactivatedEvent(false, e);
    }
  };

  useEffectOnUpdates(() => {
    setShowFeedback(true);
  }, [error, deleteLoyaltyCardSucceeded]);

  const modalHeading = formatMessage({ id: 'deactivateTimsCard' });

  return (
    <Modal
      size={ModalSize.REGULAR}
      onDismiss={onDismiss}
      mParticleEventData={{
        modalAppearanceEventMessage,
      }}
    >
      <ModalContent>
        <ModalHeading>{modalHeading}</ModalHeading>
        <CenteredParagraph>{lostOrStolenText}</CenteredParagraph>
        {deleteLoyaltyCardSucceeded ? (
          <SuccessFeedback show={showFeedback} />
        ) : (
          <FailedFeedback show={showFeedback} />
        )}
        <DeactivateCardButtonWrapper>
          {deleteLoyaltyCardSucceeded ? (
            <TextButton data-testid="close-window" onClick={onDismiss}>
              {formatMessage({ id: 'closeWindow' })}
            </TextButton>
          ) : (
            <ActionButton
              data-testid="deactivate-card-btn"
              isLoading={loading}
              onClick={handleDeactivation}
            >
              {formatMessage({ id: 'deactivateCard' })}
            </ActionButton>
          )}
        </DeactivateCardButtonWrapper>
      </ModalContent>
    </Modal>
  );
};

export default CardDeactivationModal;
